import { createPRNG } from "./prng.js";
import { buildGrid } from "./grid-builder.js";
import { shuffleGrid } from "./shuffler.js";
import { DIFFICULTIES } from "./types.js";
import type { Puzzle, WordPlacement, Difficulty } from "./types.js";
import { makeDailySeed } from "./constants.js";

/**
 * Generate a puzzle for a given date and size.
 * Uses a deterministic seed based on date+size for daily puzzles.
 * Pass a random seed for random (non-daily) puzzles.
 */
export function generatePuzzle(
  size: "7x7" | "9x9",
  seed: string,
  difficulty: Difficulty = "medium"
): Puzzle | null {
  const rng = createPRNG(seed);

  const result = buildGrid(size, rng, 100, difficulty);
  if (!result) return null;

  const diffIndex = DIFFICULTIES.indexOf(difficulty);
  const baseMinSwaps = size === "9x9" ? 26 : 14;
  const diffStep = size === "9x9" ? 2 : 1;
  const minSolveTarget = baseMinSwaps + diffIndex * diffStep;
  const { shuffled, minToSolve } = shuffleGrid(result.grid, rng, minSolveTarget, difficulty);

  const words: WordPlacement[] = result.filledSlots.map((slot) => ({
    word: slot.word,
    startRow: slot.startRow,
    startCol: slot.startCol,
    direction: slot.direction,
  }));

  // maxSwaps = optimal solution + 6 bonus moves
  const maxSwaps = minToSolve + 6;

  return {
    date: "",
    size,
    difficulty,
    grid: shuffled,
    solution: result.grid,
    words,
    maxSwaps,
  };
}

function main(): void {
  const args = process.argv.slice(2);

  let date = new Date().toISOString().split("T")[0];
  let size: "7x7" | "9x9" = "7x7";

  for (let i = 0; i < args.length; i++) {
    if (args[i] === "--date" && args[i + 1]) {
      date = args[i + 1];
      i++;
    } else if (args[i] === "--size" && args[i + 1]) {
      const s = args[i + 1];
      if (s === "7x7" || s === "9x9") {
        size = s;
      } else {
        console.error(`Invalid size: ${s}. Use "7x7" or "9x9".`);
        process.exit(1);
      }
      i++;
    }
  }

  const seed = makeDailySeed(date, size);
  const puzzle = generatePuzzle(size, seed);
  if (!puzzle) {
    console.error("Failed to generate a valid grid after maximum attempts.");
    process.exit(1);
  }

  puzzle.date = date;
  console.log(JSON.stringify(puzzle, null, 2));
}

// Only run CLI when executed directly (not when imported)
const isMainModule =
  typeof process !== "undefined" &&
  process.argv[1] &&
  (process.argv[1].endsWith("generate.ts") || process.argv[1].endsWith("generate.js"));

if (isMainModule) {
  main();
}
